import { Box, Card, CardContent, Typography, useTheme, alpha, Chip, Button } from "@mui/material";
import React from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { 
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { getTodaysProgress } from "../../api/services/notification";

const ActionProgress = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery("todaysprogress", getTodaysProgress, {
    enabled: true,
    keepPreviousData: true,
  });

  const completed = data?.completed || 0;
  const inProgress = data?.in_progress || 0;
  const pending = data?.pending || 0;
  const total = data?.total || 0;

  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

  const handleViewAll = () => {
    navigate("/operations?tab=actions");
  };

  const stats = [
    {
      label: "Completed",
      value: completed,
      icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.08),
    },
    {
      label: "In Progress",
      value: inProgress,
      icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.08),
    },
    {
      label: "Pending",
      value: pending,
      icon: <PendingIcon sx={{ fontSize: 16 }} />,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.08),
    },
  ];

  const getProgressColor = () => {
    if (completionRate >= 80) return theme.palette.success.main;
    if (completionRate >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        height: 320,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrendingUpIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
              Today's Actions
            </Typography>
            <Chip
              label={`${completionRate}%`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: alpha(getProgressColor(), 0.08),
                color: getProgressColor(),
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              {completed}/{total}
            </Typography>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              onClick={handleViewAll}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.7rem",
                color: theme.palette.primary.main,
                minWidth: "auto",
                p: 0.5,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              View
            </Button>
          </Box>
        </Box>
      </CardContent>

      <Box sx={{ px: 2, flex: 1 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : total === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              No actions scheduled for today
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              All clear! ✅
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha(theme.palette.divider, 0.3),
                width: "100%",
                display: "flex",
                overflow: "hidden",
                mb: 2,
                mt: 1,
              }}
            >
              {completed > 0 && (
                <Box
                  sx={{
                    width: `${(completed / total) * 100}%`,
                    backgroundColor: theme.palette.success.main,
                    transition: "width 0.6s ease",
                  }}
                />
              )}
              {inProgress > 0 && (
                <Box
                  sx={{
                    width: `${(inProgress / total) * 100}%`,
                    backgroundColor: theme.palette.warning.main,
                    transition: "width 0.6s ease",
                  }}
                />
              )}
              {pending > 0 && (
                <Box
                  sx={{
                    width: `${(pending / total) * 100}%`,
                    backgroundColor: theme.palette.info.main,
                    transition: "width 0.6s ease",
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 1,
                mt: 1,
              }}
            >
              {stats.map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: stat.bgColor,
                    textAlign: "center",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "scale(1.02)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                    {stat.icon}
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: stat.color, lineHeight: 1.2 }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem", fontWeight: 500 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

export default ActionProgress;